import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PendingStatus } from "@/components/ui/pending-indicator";
import {
  createDeskTodo,
  listDeskTodos,
  patchDeskTodo,
  type DeskTodo,
} from "@/shared/api/desk-todos";
import { HomeWidget } from "./HomeWidget";

/** Personal My todos list widget — plan/50 C. */
export function MyTodosWidget(props: {
  companySlug: string;
  onCustomize?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const queryKey = ["deskTodos", props.companySlug] as const;

  const listQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => listDeskTodos(props.companySlug, { signal }),
  });

  const createMut = useMutation({
    mutationFn: (title: string) =>
      createDeskTodo(props.companySlug, { title }),
    onSuccess: () => {
      setDraft("");
      void qc.invalidateQueries({ queryKey });
    },
  });

  const doneMut = useMutation({
    mutationFn: (id: string) =>
      patchDeskTodo(props.companySlug, id, { done: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
  });

  const todos = listQuery.data?.todos ?? [];
  const busy = createMut.isPending || doneMut.isPending;

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    const title = draft.trim();
    if (!title || busy) return;
    createMut.mutate(title);
  }

  return (
    <HomeWidget
      title="My todos"
      description="Personal checklist for this company. Not the same as Act."
      onCustomize={props.onCustomize}
    >
      {listQuery.isPending ? (
        <PendingStatus>Loading todos…</PendingStatus>
      ) : (
        <>
          {todos.length === 0 ? (
            <p className="m-0 text-sm text-[var(--color-muted)]">
              Nothing personal queued — add a reminder below.
            </p>
          ) : (
            <ul className="m-0 list-none space-y-2 p-0">
              {todos.map((todo) => (
                <TodoRow
                  key={todo.id}
                  todo={todo}
                  disabled={busy}
                  onDone={() => doneMut.mutate(todo.id)}
                />
              ))}
            </ul>
          )}
          <form
            className="mt-3 flex flex-wrap items-center gap-2"
            onSubmit={(e) => void onAdd(e)}
          >
            <Input
              className="min-w-0 flex-1"
              placeholder="Add a todo…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
              maxLength={200}
            />
            <Button
              type="submit"
              size="sm"
              pending={createMut.isPending}
              disabled={!draft.trim() || busy}
            >
              Add
            </Button>
          </form>
          {createMut.isError ? (
            <p className="mt-2 m-0 text-xs text-[var(--color-destructive)]">
              {createMut.error instanceof Error
                ? createMut.error.message
                : "Could not add"}
            </p>
          ) : null}
        </>
      )}
    </HomeWidget>
  );
}

function TodoRow(props: {
  todo: DeskTodo;
  disabled?: boolean;
  onDone: () => void;
}) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1"
        checked={false}
        disabled={props.disabled}
        aria-label={`Mark done: ${props.todo.title}`}
        onChange={() => props.onDone()}
      />
      <span className="min-w-0 flex-1">
        {props.todo.linkPath ? (
          <Link
            to={props.todo.linkPath}
            className="font-medium text-[var(--color-foreground)] no-underline hover:underline"
          >
            {props.todo.title}
          </Link>
        ) : (
          <span className="font-medium text-[var(--color-foreground)]">
            {props.todo.title}
          </span>
        )}
      </span>
    </li>
  );
}
